// @ts-nocheck
import * as React from "react";
import { Logo, siteName, brandParts, siteRegion, BRAND_IMG, NCARE, Ic, Sidebar, Topbar, LineChart, BarChart, Kpi, useCurrentUser, logout, AppFooter, PhoneInput, apiErrorMessage, fmtDuration } from "./common";
import { publicNav, MobileNavMenu, useIsNarrow } from "./public-pages";
import { NaverMapCanvas } from "../components/naver/NaverMapCanvas";
import { useNaverMarkers } from "../components/naver/useNaverMarkers";
import { MarkerGlyph } from "../components/naver/MarkerGlyph";
import { MARKER_HEX, glyphMarkup } from "../components/naver/markers";

const CAT_COLOR = { hospital: "rose", health: "green", fire: "orange", welfare: "sky", care: "amber" };
const CAT_LABEL = { hospital: "병", health: "보", fire: "소", welfare: "복", care: "요" };
const RISK_COLOR = { critical: "rose", warning: "amber", stable: "green" };
const MGR_STATUS_COLOR = { available: "green", overloaded: "rose", busy: "amber", moving: "amber", offline: "gray" };

// 랜딩 "권역 운영 지도" — 가짜 SVG 투영을 실제 네이버 지도로 교체.
// 공개 페이지의 쇼케이스 티저라 조작은 잠그고(interactive=false) "지도 전체 보기 →"
// (/map)로 위임한다. hero/feature 아래 fold 밖이라 IntersectionObserver lazy 마운트로
// 첫 페인트 시 타일 요청을 미룬다. 데이터(/api/v1/map/points)는 비로그인 anon-safe라
// 익명 방문자에겐 좌표+분류/위험도만(이름 등 PII는 null) 내려온다.
// minFitZoom: 익명 기본 권역(서울)은 bbox가 작은데 카드가 가로로 넓어, 순수
// fitBounds는 세로에 맞추느라 z10까지 줌아웃 → 서울이 주변 경기에 파묻혀 보인다.
// z11 바닥을 줘 서울이 프레임을 채우게 한다(중심 유지, 서울 전 기관 여전히 노출).
function LandingRegionMap({ mapData }) {
  const wrapRef = React.useRef(null);
  const [inView, setInView] = React.useState(false);
  const [map, setMap] = React.useState(null);

  React.useEffect(() => {
    if (inView) return;
    const el = wrapRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setInView(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) { setInView(true); io.disconnect(); }
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  const center = mapData?.center;
  const bbox = mapData?.bbox || null;

  const sig = React.useMemo(() => {
    if (!mapData) return "";
    return JSON.stringify({
      o: (mapData.organizations || []).map(x => [x.id, x.lat, x.lng, x.category]),
      e: (mapData.elderlies || []).map(x => [x.id, x.lat, x.lng, x.risk_level]),
      m: (mapData.care_managers || []).map(x => [x.id, x.lat, x.lng, x.status]),
    });
  }, [mapData]);

  // 권역 운영 지도 마커 — 다른 지도(/map·대시보드)와 동일하게 공유 useNaverMarkers
  // 훅에 위임해 그리드 클러스터링(겹치는 마커를 묶음 숫자 원으로 표시)을 적용한다.
  // 쇼케이스 티저라 전부 노출(rendered)·비강조(dim=false)·선택상태 없음.
  // clustering:true여도 같은 셀에 2개 이상 겹칠 때만 묶이므로 희소 지역은 개별 유지.
  const allVisible = (list) => {
    const m = new Map();
    (list || []).forEach(x => { if (x.lat != null && x.lng != null) m.set(x.id, { rendered: true, dim: false }); });
    return m;
  };
  const orgVis     = React.useMemo(() => allVisible(mapData?.organizations), [sig]);
  const elderlyVis = React.useMemo(() => allVisible(mapData?.elderlies),     [sig]);
  const managerVis = React.useMemo(() => allVisible(mapData?.care_managers), [sig]);

  useNaverMarkers({
    map,
    data: mapData,
    orgVisibility: orgVis,
    elderlyVisibility: elderlyVis,
    managerVisibility: managerVis,
    selected: null,
    onSelect: () => {},
    clustering: true,
  });

  // wrapRef는 항상 렌더해야 IntersectionObserver가 첫 마운트에 붙는다(데이터 로딩 분기에서
  // early-return하면 ref가 null이라 observer가 영영 안 붙고 inView가 false로 고정됨).
  return (
    <div ref={wrapRef} style={{position:'absolute', inset:0}}>
      {!mapData
        ? <div className="muted" style={{position:'absolute', inset:0, display:'grid', placeItems:'center'}}>지도 데이터 불러오는 중…</div>
        : (inView && center)
          ? <NaverMapCanvas center={center} bbox={bbox} zoom={13} interactive={false} fitPadding={30} minFitZoom={11} onMapReady={setMap}/>
          : <div style={{position:'absolute', inset:0, display:'grid', placeItems:'center'}}>
              <span className="muted" style={{fontSize:12, fontWeight:600, background:'rgba(255,255,255,0.85)', padding:'6px 12px', borderRadius:8}}>지도 준비 중…</span>
            </div>}
    </div>
  );
}

function LandingDesktop() {
  const [stats, setStats] = React.useState(null);
  const [mapData, setMapData] = React.useState(null);
  const { user } = useCurrentUser();
  // 좁은 화면(태블릿 세로 이하)에서는 상단 메뉴를 햄버거 드로어로 접는다.
  const narrow = useIsNarrow(860);
  // 사이트 권역(남양주) — 메타 태그가 단일 출처. 통계가 오면 서버 값으로 덮어쓴다.
  const region = siteRegion();

  React.useEffect(() => {
    fetch("/api/v1/landing/stats").then(r => r.json()).then(setStats);
    fetch("/api/v1/map/points").then(r => r.json()).then(setMapData);
  }, []);

  const site       = stats?.site || null;
  const regionName = site?.name || region.name;              // "남양주시"
  const cityName   = regionName.replace(/시$/, "");           // "남양주"
  const num        = (v) => (v == null ? '·' : v.toLocaleString());

  return (
    <div className="app" style={{background:'var(--bg)', overflow:'hidden'}}>
      {/* Top nav — 넓은 화면은 인라인 메뉴, 좁은 화면은 햄버거 드로어(MobileNavMenu).
          (과거: 고정 flex라 390px에서 메뉴 글자가 세로로 짜부라졌다) */}
      <header style={{display:'flex', alignItems:'center', flexWrap:'wrap', rowGap:10, padding:'16px clamp(18px, 5vw, 64px)', borderBottom:'1px solid var(--border-soft)', background:'var(--surface)'}}>
        <a href="/" style={{display:'flex', alignItems:'center', gap:11, textDecoration:'none', color:'inherit'}}>
          {/* 브랜드 마크 = N-Care 2.0 심볼(제공 이미지에서 잘라낸 것) */}
          <img src={BRAND_IMG.mark} alt="" width={38} height={38}
               style={{width:38, height:38, borderRadius:11, objectFit:'cover', display:'block', boxShadow:'0 2px 8px rgba(22,50,79,0.22)'}}/>
          <div>
            <div style={{fontWeight:800, fontSize:15, color:NCARE.navy, letterSpacing:'-0.01em'}}>{cityName}형 통합돌봄</div>
            <div style={{fontSize:10.5, color:NCARE.accent, letterSpacing:'0.1em', fontWeight:700, marginTop:-1}}>N-CARE 2.0</div>
          </div>
        </a>
        {/* 상단 메뉴는 공개 소개 페이지(public-pages.tsx)와 PUBLIC_NAV 한 배열을 공유한다. */}
        {!narrow && (
          <nav style={{display:'flex', gap:'clamp(14px, 2.2vw, 28px)', marginLeft:'clamp(16px, 4vw, 56px)', fontSize:13, color:'var(--ink-2)', fontWeight:500,
                       flex:'1 1 200px', minWidth:0, overflowX:'auto', whiteSpace:'nowrap', WebkitOverflowScrolling:'touch'}}>
            {publicNav().map(n => (
              <a key={n.href} href={n.href} style={{color:'inherit', textDecoration:'none'}}>{n.label}</a>
            ))}
          </nav>
        )}
        <div style={{marginLeft:'auto', display:'flex', gap:10, alignItems:'center'}}>
          {narrow
            ? <MobileNavMenu/>
            : user ? <UserMenu user={user}/> : (
                <>
                  <a href="/login" className="btn btn-ghost btn-md">로그인</a>
                  <a href="/signup" className="btn btn-md" style={{background:NCARE.navy, color:'#fff', border:'1px solid '+NCARE.navy}}>회원가입</a>
                </>
              )}
        </div>
      </header>

      {/* Hero — 넓은 화면: 표지 이미지(원본)를 배경으로 전면에 깐다. 표지 안에 제목·부제·
          로고·슬로건이 이미 들어 있어 우리 카피는 얹지 않고, 빈 좌하단 여백에 CTA만 놓는다.
          좁은 화면: 표지 글자가 읽히지 않는 크기라 카피 + 사진 패널 스택으로 폴백한다. */}
      {!narrow ? (
        <section aria-label={`2026 ${cityName}형 통합돌봄 N-Care 2.0 하이브리드 전략`}
                 style={{position:'relative', width:'100%', maxWidth:1400, margin:'0 auto',
                         aspectRatio:'1835 / 980',
                         backgroundImage:`url(${BRAND_IMG.cover})`, backgroundSize:'cover',
                         backgroundPosition:'center', backgroundRepeat:'no-repeat'}}>
          {/* 좌하단 흰 여백 — 표지 부제 아래는 비어 있어 CTA를 얹어도 글자와 겹치지 않는다. */}
          <div style={{position:'absolute', left:'4.6%', top:'70%', display:'flex', gap:12, flexWrap:'wrap'}}>
            <a href={user ? user.default_path : "/login"} className="btn btn-lg"
               style={{textDecoration:'none', background:NCARE.navy, color:'#fff', border:'1px solid '+NCARE.navy}}>
              {user ? "내 화면으로 이동 →" : "로그인하고 시작하기 →"}
            </a>
            {!user && <a href="/signup" className="btn btn-lg"
                         style={{textDecoration:'none', background:'rgba(255,255,255,0.9)', color:NCARE.navy, border:'1px solid '+NCARE.soft2}}>회원가입 · 케어 신청</a>}
          </div>
        </section>
      ) : (
        <section style={{padding:'clamp(26px, 5vw, 44px) clamp(18px, 5vw, 64px) clamp(20px, 3vw, 32px)'}}>
          <div style={{fontSize:13, fontWeight:700, color:NCARE.accent, marginBottom:12}}>2026 Namyangju Strategic Initiative</div>
          <h1 style={{fontSize:'clamp(26px, 6.6vw, 38px)', lineHeight:1.25, wordBreak:'keep-all', letterSpacing:'-0.03em', fontWeight:800, color:NCARE.navy, margin:0}}>
            2026 {cityName}형 통합돌봄<br/>
            <span style={{color:NCARE.ink}}>‘N-Care 2.0’</span> 하이브리드 전략
          </h1>
          <p style={{fontSize:14, lineHeight:1.7, color:'var(--ink-2)', marginTop:18, wordBreak:'keep-all'}}>
            초고령사회와 「돌봄통합지원법」 시행에 따른 <b style={{color:'var(--ink-1)'}}>민관협력 실행 로드맵</b>.
            신청·매칭·방문·기록을 하나의 플랫폼으로 운영합니다.
          </p>
          <img src={BRAND_IMG.hero}
               alt={`${cityName} N-Care 2.0 — 살던 곳에서 365일 존엄하게, ${cityName} N-Care가 함께합니다`}
               style={{width:'100%', height:'auto', display:'block', marginTop:20, borderRadius:20,
                       border:'1px solid var(--border-soft)', boxShadow:'0 20px 44px -26px rgba(22,50,79,0.55)'}}/>
          <div style={{display:'flex', gap:10, marginTop:20, flexWrap:'wrap'}}>
            <a href={user ? user.default_path : "/login"} className="btn btn-lg"
               style={{textDecoration:'none', background:NCARE.navy, color:'#fff', border:'1px solid '+NCARE.navy}}>
              {user ? "내 화면으로 이동 →" : "로그인하고 시작하기 →"}
            </a>
            {!user && <a href="/signup" className="btn btn-ghost btn-lg" style={{textDecoration:'none'}}>회원가입 · 케어 신청</a>}
          </div>
        </section>
      )}

      {/* 실시간 수치 — 표지 아래 공통 스트립 */}
      <section style={{padding:'clamp(20px, 3vw, 32px) clamp(18px, 5vw, 64px) clamp(12px, 2vw, 20px)'}}>
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(110px, 1fr))', gap:'clamp(12px, 2vw, 28px)',
                     paddingTop:22, borderTop:'1px solid var(--border-soft)'}}>
          <Stat n={num(site?.partner_orgs)}  l={`${cityName} 유관기관`}/>
          <Stat n={num(site?.care_managers)} l="케어매니저"/>
          <Stat n={num(site?.elderlies)}     l="등록 어르신"/>
          <Stat n={num(site?.open_cases)}    l="진행 중 케이스"/>
        </div>
      </section>
      {/* 4대 실행축 */}
      <section id="features" style={{padding:'clamp(18px, 3vw, 28px) clamp(18px, 5vw, 64px) clamp(40px, 5vw, 60px)'}}>
        <div style={{fontSize:11, fontWeight:800, color:NCARE.accent, letterSpacing:'0.08em', marginBottom:6}}>N-CARE 2.0 · 4대 실행축</div>
        <div className="h2" style={{fontSize:24, letterSpacing:'-0.02em', marginBottom:18, color:NCARE.navy}}>재가완결형 돌봄을 실행하는 방법</div>
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(230px, 1fr))', gap:18}}>
          {[
            {t:'민관협력 실행거점', d:'공공·민간 유관기관이 같은 화면에서 케이스를 함께 운영합니다.', i:'region', href:'/about'},
            {t:'AI 자동매칭 (COD)', d:'어르신 상태·자격·거리·매니저 부하를 가중치로 평가해 최적 케어매니저를 추천합니다.', i:'match', href:'/cod'},
            {t:'지역 자원은행',     d:'요양·의료·복지 기관의 가용 현황을 모아 조건에 맞는 기관에 서비스를 의뢰합니다.', i:'org', href:'/resources'},
            {t:'재가완결형 돌봄',   d:'방문·복약·활력 기록과 보호자 알림으로 살던 곳에서 365일 이어지는 돌봄을 지원합니다.', i:'heart', href:'/m/notify'},
          ].map((f,i)=>(
            <a key={i} href={f.href} className="card card-pad" style={{borderRadius:18, textDecoration:'none', color:'inherit'}}>
              <span style={{width:40, height:40, borderRadius:12, background:NCARE.soft, color:NCARE.navy2, display:'grid', placeItems:'center'}}>
                <span style={{width:22, height:22, display:'inline-block'}}>{Ic[f.i]}</span>
              </span>
              <div style={{fontWeight:700, fontSize:15, marginTop:14, color:NCARE.navy}}>{f.t}</div>
              <div style={{fontSize:12, color:'var(--ink-3)', lineHeight:1.6, marginTop:6, wordBreak:'keep-all'}}>{f.d}</div>
              <div style={{fontSize:11, color:NCARE.accent, fontWeight:700, marginTop:10}}>자세히 →</div>
            </a>
          ))}
        </div>
      </section>

      {/* 비로그인 — 케어 신청(회원가입) 안내 밴드 */}
      {!user && <CareSignupBand cityName={cityName}/>}

      {/* Region map — live markers from DB */}
      <section id="map-section" style={{padding:'24px clamp(18px, 5vw, 64px) clamp(40px, 5vw, 60px)'}}>
        <div style={{display:'flex', alignItems:'flex-end', justifyContent:'space-between', gap:12, flexWrap:'wrap', marginBottom:18}}>
          <div>
            <div style={{fontSize:11, fontWeight:800, color:NCARE.accent, letterSpacing:'0.08em', marginBottom:6}}>{cityName} 돌봄 자원 지도</div>
            <div className="h2" style={{fontSize:24, letterSpacing:'-0.02em', color:NCARE.navy}}>{regionName} 안의 매니저·기관·어르신을 한 눈에</div>
            <div className="muted" style={{fontSize:13, marginTop:6}}>{mapData ? `유관기관 ${mapData.organizations.length.toLocaleString()} · 케어매니저 ${mapData.care_managers.length.toLocaleString()} · 어르신 ${mapData.elderlies.length.toLocaleString()}` : '로드 중…'}</div>
          </div>
          <a href="/map" className="btn btn-ghost btn-md" style={{textDecoration:'none'}}>지도 전체 보기 →</a>
        </div>

        <div className="card" style={{display:'flex', flexWrap:'wrap', borderRadius:18, overflow:'hidden', border:'1px solid var(--border-soft)'}}>
          {/* Map area — 좁아지면 범례가 지도 아래로 내려간다(그리드 고정 시 지도 폭이 0에 수렴). */}
          <div style={{position:'relative', flex:'1 1 380px', minWidth:0, background:'#E7E1CF', minHeight:'clamp(300px, 46vw, 480px)', overflow:'hidden'}}>
            <LandingRegionMap mapData={mapData}/>
          </div>
          {/* Legend panel */}
          <div className="landing-map-legend" style={{padding:'22px 22px', flex:'1 1 280px', minWidth:0, background:'var(--surface)', display:'flex', flexDirection:'column'}}>
            <div style={{fontSize:11, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em', marginBottom:10}}>범례</div>

            {/* Care managers */}
            <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:12}}>
              <div style={{width:30, height:30, borderRadius:'50%', background:'linear-gradient(135deg, var(--orange-300), var(--orange-500))', display:'grid', placeItems:'center', border:'2px solid #fff', boxShadow:'0 2px 6px rgba(0,0,0,0.18)'}}>
                <MarkerGlyph glyph="manager" size={16} bg={MARKER_HEX.manager}/>
              </div>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:12.5, fontWeight:700}}>케어매니저</div>
                <div className="muted" style={{fontSize:10}}>실시간 위치 + 가용 상태</div>
              </div>
              <span className="mono" style={{fontSize:11, fontWeight:700}}>{(mapData?.care_managers.length ?? 0).toLocaleString()}</span>
            </div>

            {/* Status dots */}
            <div style={{display:'flex', gap:8, padding:'8px 10px', background:'var(--bg-sub)', borderRadius:8, marginBottom:14, fontSize:10, color:'var(--ink-3)'}}>
              <span style={{display:'flex', alignItems:'center', gap:4}}><span className="dot" style={{background:'var(--green-500)'}}/> 가용</span>
              <span style={{display:'flex', alignItems:'center', gap:4}}><span className="dot" style={{background:'var(--amber-500)'}}/> 이동중</span>
              <span style={{display:'flex', alignItems:'center', gap:4}}><span className="dot" style={{background:'var(--rose-500)'}}/> 과부하</span>
            </div>

            {/* Elderly */}
            <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:12}}>
              {/* 지도 어르신 마커의 축소판 — 핀 + 지팡이 짚은 사람. */}
              <svg width="22" height="28" viewBox="0 0 28 36" style={{width:22, height:28, flex:'none'}}>
                <path d="M14 0C6.3 0 0 6.3 0 14c0 10 14 22 14 22s14-12 14-22C28 6.3 21.7 0 14 0Z" fill="var(--rose-500)" stroke="#fff" strokeWidth="2"/>
                <g transform="translate(7.1,7.1) scale(0.575)"
                   dangerouslySetInnerHTML={{__html: glyphMarkup('elderly')}}/>
              </svg>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:12.5, fontWeight:700}}>어르신 케이스</div>
                <div className="muted" style={{fontSize:10}}>위험도별 색상</div>
              </div>
              <span className="mono" style={{fontSize:11, fontWeight:700}}>{(mapData?.elderlies.length ?? 0).toLocaleString()}</span>
            </div>
            <div style={{display:'flex', gap:8, padding:'8px 10px', background:'var(--bg-sub)', borderRadius:8, marginBottom:14, fontSize:10, color:'var(--ink-3)'}}>
              <span style={{display:'flex', alignItems:'center', gap:4}}><span className="dot" style={{background:'var(--rose-500)'}}/> 위험</span>
              <span style={{display:'flex', alignItems:'center', gap:4}}><span className="dot" style={{background:'var(--amber-500)'}}/> 주의</span>
              <span style={{display:'flex', alignItems:'center', gap:4}}><span className="dot" style={{background:'var(--green-500)'}}/> 안정</span>
            </div>

            {/* Organizations by category */}
            <div style={{fontSize:11, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em', marginTop:4, marginBottom:8}}>유관기관 ({(mapData?.organizations.length ?? 0).toLocaleString()})</div>
            {[
              { key: 'hospital', label: '병원·의원',     icon: 'hospital' },
              { key: 'health',   label: '보건소·보건지소', icon: 'health' },
              { key: 'fire',     label: '소방·119',     icon: 'fire' },
              { key: 'welfare',  label: '복지관·주민센터', icon: 'welfare' },
              { key: 'care',     label: '요양기관',      icon: 'care' },
            ].map(c => {
              const count = (mapData?.organizations || []).filter(o => o.category === c.key).length;
              const hex = MARKER_HEX.category[c.key];
              return (
                <div key={c.key} style={{display:'flex', alignItems:'center', gap:10, padding:'7px 0', borderTop:'1px solid var(--border-soft)'}}>
                  {/* 지도 마커와 같은 색·픽토그램(markers.ts 단일 출처). */}
                  <span style={{width:24, height:24, borderRadius:6, background:hex, display:'grid', placeItems:'center', boxShadow:'0 2px 4px rgba(0,0,0,0.12)'}}>
                    <MarkerGlyph glyph={c.icon} size={13} bg={hex}/>
                  </span>
                  <span style={{flex:1, fontSize:12}}>{c.label}</span>
                  <span className="mono muted" style={{fontSize:11}}>{count.toLocaleString()}</span>
                </div>
              );
            })}

            <div style={{flex:1}}/>
            <div style={{marginTop:16, padding:'8px 10px', background:NCARE.soft, color:NCARE.navy2, borderRadius:8, fontSize:10.5, lineHeight:1.5}}>
              ⓘ 권역 경계는 <b>점선</b>으로 표시됩니다. {regionName} 안의 모든 돌봄 자원이 한 화면에 모입니다.
            </div>
          </div>
        </div>
      </section>

      {/* 운영 현황 — 사이트 권역(남양주) 실시간 수치 */}
      <section id="partners" style={{padding:'0 clamp(18px, 5vw, 64px) clamp(40px, 5vw, 60px)'}}>
        <div className="card" style={{padding:'clamp(22px, 3vw, 32px) clamp(20px, 3vw, 36px)', borderRadius:18,
             background:`linear-gradient(120deg, ${NCARE.navy} 0%, ${NCARE.navy2} 55%, ${NCARE.navy3} 100%)`, border:'none', color:'#fff'}}>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:'clamp(20px, 3vw, 32px)', alignItems:'center'}}>
            <div>
              <div style={{fontSize:11, fontWeight:800, color:'#9FC4E0', letterSpacing:'0.08em', marginBottom:8}}>{cityName} 운영 현황</div>
              <div className="h2" style={{fontSize:22, color:'#fff', lineHeight:1.4}}>살던 곳에서 365일 존엄하게,<br/>{cityName} N-Care가 함께합니다</div>
            </div>
            {/* 이달 매칭 시도(monthly_matches)는 이번 달 신규 매칭이 없으면 0으로 떠
                랜딩에서 오해를 부른다 — 항상 실체가 있는 3개 지표만 노출한다. */}
            <KpiCol light n={num(site?.partner_orgs)}            l="참여 유관기관"/>
            <KpiCol light n={num(site?.open_cases)}              l="진행 중 케이스"/>
            <KpiCol light n={site ? `${site.success_rate}%` : '·'} l="매칭 성공률"/>
          </div>
        </div>
      </section>

      <AppFooter/>
    </div>
  );
}

function Stat({n, l}) {
  return (
    <div>
      <div style={{fontSize:24, fontWeight:800, color:'var(--ink-1)', fontVariantNumeric:'tabular-nums'}}>{n}</div>
      <div style={{fontSize:11, color:'var(--ink-3)'}}>{l}</div>
    </div>
  );
}
// light: 네이비 배경 카드 위에 얹을 때(흰 글자). 기본은 밝은 카드용.
function KpiCol({n, l, light}) {
  return (
    <div>
      <div style={{fontSize:32, fontWeight:800, color: light ? '#fff' : NCARE.navy2, fontVariantNumeric:'tabular-nums', lineHeight:1}}>{n}</div>
      <div style={{fontSize:12, color: light ? '#9FC4E0' : 'var(--ink-3)', marginTop:6, fontWeight:500}}>{l}</div>
    </div>
  );
}

// ─── 케어 신청 안내 밴드 ──────────────────────────────────
// 케어 신청은 회원가입으로만 접수한다(별도 공개 신청 폼 없음). 비로그인 방문자에게
// 3단계 흐름과 회원가입 동선을 히어로 아래 가로 밴드로 안내한다.
function CareSignupBand({ cityName }) {
  const STEPS = [
    { n: '01', t: '회원가입 · 케어 신청', d: '보호자·어르신으로 가입하며 어르신 정보를 등록하면 케어 신청이 함께 접수됩니다.' },
    { n: '02', t: 'AI 매칭 추천',        d: `${cityName} 통합돌봄 운영자가 검토하고 AI가 추천한 케어매니저를 배정합니다.` },
    { n: '03', t: '방문 · 알림',          d: '매니저가 방문하고 활력·복약·기록이 보호자에게 실시간으로 공유됩니다.' },
  ];
  return (
    <section style={{padding:'0 clamp(18px, 5vw, 64px) clamp(40px, 5vw, 56px)'}}>
      <div className="card" style={{padding:'clamp(22px, 3vw, 32px) clamp(20px, 3vw, 36px)', borderRadius:22, border:'1px solid '+NCARE.soft2, background:'var(--surface)'}}>
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:'clamp(18px, 3vw, 32px)', alignItems:'center'}}>
          <div>
            <div style={{fontSize:11, fontWeight:800, color:NCARE.accent, letterSpacing:'0.08em', marginBottom:8}}>케어 신청</div>
            <div className="h2" style={{fontSize:22, lineHeight:1.4, color:NCARE.navy}}>회원가입 한 번으로<br/>{cityName} 통합돌봄을 신청하세요</div>
            <div className="muted" style={{fontSize:12.5, lineHeight:1.65, marginTop:10, wordBreak:'keep-all'}}>
              가입 후 홈 화면에서 신청 상태(승인 대기·케이스 준비 중)와 배정된 매니저를 직접 확인할 수 있습니다.
            </div>
            <a href="/signup" className="btn btn-lg" style={{marginTop:18, textDecoration:'none', display:'inline-flex', background:NCARE.navy, color:'#fff', border:'1px solid '+NCARE.navy}}>
              회원가입하고 케어 신청 →
            </a>
            <div style={{fontSize:11, color:'var(--ink-3)', marginTop:10}}>
              이미 회원이신가요? <a href="/login" style={{color:NCARE.navy2, fontWeight:700, textDecoration:'none'}}>로그인</a>
            </div>
          </div>
          {STEPS.map(s => (
            <div key={s.n} style={{display:'flex', gap:12, alignItems:'flex-start'}}>
              <div style={{flex:'0 0 40px', height:40, borderRadius:12, background:NCARE.soft, color:NCARE.navy2, display:'grid', placeItems:'center', fontSize:13, fontWeight:800}}>{s.n}</div>
              <div style={{minWidth:0}}>
                <div style={{fontSize:14, fontWeight:800, color:'var(--ink-1)'}}>{s.t}</div>
                <div className="muted" style={{fontSize:12, lineHeight:1.6, marginTop:4, wordBreak:'keep-all'}}>{s.d}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


function UserMenu({ user }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  const initial = (user.name || "?").charAt(0);
  return (
    <div ref={ref} style={{position:'relative', display:'flex', alignItems:'center', gap:8}}>
      <a href={user.default_path} className="btn btn-primary btn-md" style={{textDecoration:'none'}}>내 화면으로 →</a>
      <button onClick={()=>setOpen(o=>!o)}
        style={{display:'flex', alignItems:'center', gap:8, padding:'4px 10px 4px 4px',
          borderRadius:999, border:'1px solid var(--border-soft)', background:'var(--surface)',
          cursor:'pointer'}}>
        <span style={{width:28, height:28, borderRadius:'50%',
          background:'linear-gradient(135deg, var(--orange-400), var(--orange-600))',
          color:'#fff', display:'grid', placeItems:'center', fontSize:12, fontWeight:800}}>{initial}</span>
        <span style={{display:'flex', flexDirection:'column', alignItems:'flex-start', lineHeight:1.15}}>
          <span style={{fontSize:12, fontWeight:700, color:'var(--ink-1)'}}>{user.name}</span>
          <span style={{fontSize:10, color:'var(--ink-3)'}}>{user.role_label}</span>
        </span>
        <span style={{color:'var(--ink-3)', marginLeft:2}}>{Ic.caret}</span>
      </button>
      {open && (
        <div style={{position:'absolute', top:'calc(100% + 8px)', right:0, minWidth:200,
          background:'var(--surface)', border:'1px solid var(--border-soft)', borderRadius:12,
          boxShadow:'0 12px 32px rgba(20,16,12,0.12)', padding:6, zIndex:30}}>
          <div style={{padding:'10px 12px 8px', borderBottom:'1px solid var(--border-soft)'}}>
            <div style={{fontSize:12, fontWeight:700, color:'var(--ink-1)'}}>{user.name}</div>
            <div style={{fontSize:11, color:'var(--ink-3)', marginTop:2}}>{user.email}</div>
          </div>
          <a href={user.default_path} style={{display:'block', padding:'10px 12px', fontSize:12.5,
            color:'var(--ink-1)', textDecoration:'none', borderRadius:8}}
            onMouseEnter={e=>e.currentTarget.style.background='var(--bg-sub)'}
            onMouseLeave={e=>e.currentTarget.style.background='transparent'}>내 화면으로 이동</a>
          <button onClick={logout} style={{width:'100%', textAlign:'left', padding:'10px 12px',
            fontSize:12.5, color:'var(--rose-500)', background:'transparent', border:'none',
            borderRadius:8, cursor:'pointer'}}
            onMouseEnter={e=>e.currentTarget.style.background='var(--bg-sub)'}
            onMouseLeave={e=>e.currentTarget.style.background='transparent'}>로그아웃</button>
        </div>
      )}
    </div>
  );
}

export { LandingDesktop };
